import {useNavigate} from "react-router-dom";
import {useContext} from "react";
import {ChangesContext, ToastContext} from "../Contexts";
import {
    GalleriesFilterResponse, Gallery, GalleryFilterRequest, GallerySaveRequest, GalleryUsageResponse,
    ToastFailureMessage,
    ToastSuccessMessage
} from "../Models/Models";
import HttpRepository from "./HttpRepository";

/** Reshapes the UI gallery state into the backend's BaseGalleryRequest contract. */
const toGallerySaveRequest = (gallery: Gallery): GallerySaveRequest => ({
    id: gallery.id,
    title: gallery.title,
    websiteId: gallery.websiteId,
    titleImageUsageId: gallery.titleImage?.id ?? 0,
    titleImageUsage: {
        id: gallery.titleImage?.id ?? 0,
        imageId: gallery.titleImage?.imageId ?? 0,
        filePath: gallery.titleImage?.filePath ?? '',
    },
    galleryImages: gallery.galleryImages.map(usage => ({
        id: usage.imageId,
        filePath: usage.filePath,
        alt: usage.alt,
    })),
    coordinates: gallery.coordinates,
    visibility: gallery.visibility,
});

export function GalleryService() {
    const navigate = useNavigate()
    let {setToast}: any = useContext(ToastContext);
    let { setHasChanges } : any = useContext(ChangesContext);

    async function createGallery(gallery: Gallery, isPopUp: boolean, onClose: any): Promise<void> {
        try {
            await HttpRepository.createGallery(toGallerySaveRequest(gallery));
            setHasChanges(true);
        }
        catch (error) {
            setToast({message: ToastFailureMessage.dataNotSaved});
            return;
        }

        setToast({message: ToastSuccessMessage.dataSaved});

        if(isPopUp) {
            onClose();
            return;
        }

        navigateToGalleryTable();
    }

    async function getGallery(id: number): Promise<Gallery> {
        try {
            return (await HttpRepository.getGallery(id)).data;
        }
        catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
        }
    }

    async function filterGalleries(request: GalleryFilterRequest): Promise<GalleriesFilterResponse> {
        try {
            return (await HttpRepository.filterGalleries(request)).data;
        }
        catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
        }
    }

    async function updateGallery(gallery: Gallery): Promise<void> {
        try {
            await HttpRepository.updateGallery(toGallerySaveRequest(gallery));
            setHasChanges(true);
        }
        catch (error) {
            setToast({message: ToastFailureMessage.dataNotSaved});
            return;
        }

        setToast({message: ToastSuccessMessage.dataSaved});
        navigateToGalleryTable();
    }

    async function checkGalleryUsage(id: number): Promise<GalleryUsageResponse> {
        try {
            return (await HttpRepository.checkGalleryUsage(id)).data;
        } catch (error) {
            setToast({message: ToastFailureMessage.error});
        }
    }

    async function deleteGallery(id: number) {
        try {
            await HttpRepository.deleteGallery(id);
            setHasChanges(true);
        }
        catch (error) {
            setToast({message: ToastFailureMessage.changesNotSaved});
            return;
        }

        setToast({message: ToastSuccessMessage.changesSaved});
        navigateToGalleryTable();
    }

    const navigateToGalleryTable = () => {
        navigate("/gallery/table");
    };

    return {filterGalleries, createGallery, getGallery, updateGallery, checkGalleryUsage, deleteGallery}
}